import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import { Check, Edit3, Sparkles, X } from "lucide-react";
import { useState } from "react";
import {
  APP_NAME_SLUG_MAX_LENGTH_WITH_SUFFIX,
  APP_NAME_SLUG_MIN_LENGTH,
  validateAppSlug,
} from "../../../../../schemas/app-name";
import type { FpMessagePart } from "../../../../../schemas/codegen";
import { ExpandableMessageCard, SimpleMessageCard } from "./shared";

interface GenerateAppNameProps {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
}

interface AppNameResult {
  success: boolean;
  data?: {
    displayName: string;
    slug: string;
  };
  message?: string;
}

export function GenerateAppName({ part }: GenerateAppNameProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedDisplayName, setEditedDisplayName] = useState("");
  const [editedSlug, setEditedSlug] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const activeProjectId = useActiveProjectId();
  const { toast } = useToast();

  const isComplete = part.status === "complete";
  const result = part.result as AppNameResult | undefined;
  const appNameData = result?.data;

  const displayName = (
    <span className="inline-flex items-center gap-2">
      <span className="text-muted-foreground">
        {part.status === "pending"
          ? "Generating app name..."
          : part.status === "complete"
            ? "Generated app name"
            : "Failed to generate app name"}
      </span>
    </span>
  );

  const handleEdit = () => {
    if (appNameData) {
      setEditedDisplayName(appNameData.displayName);
      setEditedSlug(appNameData.slug);
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedDisplayName("");
    setEditedSlug("");
  };

  const handleSave = async () => {
    if (!activeProjectId || !editedDisplayName.trim() || !editedSlug.trim()) {
      toast({
        title: "Error",
        description: "Please fill in both fields",
        variant: "destructive",
      });
      return;
    }

    if (!validateAppSlug(editedSlug)) {
      toast({
        title: "Error",
        description: `Slug must be lowercase, alphanumeric with hyphens only (${APP_NAME_SLUG_MIN_LENGTH}-${APP_NAME_SLUG_MAX_LENGTH_WITH_SUFFIX} chars)`,
        variant: "destructive",
      });
      return;
    }

    setIsUpdating(true);
    try {
      const response = await fetch(`/api/codegen/${activeProjectId}/app-name`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          appName: editedSlug,
        }),
      });

      if (!response.ok) {
        let errorMessage = "Failed to update app name";
        try {
          const errorData = await response.json();
          if (errorData.error?.message) {
            errorMessage = errorData.error.message;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // If we can't parse the response, use the status text or default message
          errorMessage = response.statusText || "Failed to update app name";
        }
        throw new Error(errorMessage);
      }

      toast({
        title: "Success",
        description: "App name updated successfully",
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating app name:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update app name";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Simple card for non-complete states
  if (!isComplete || !appNameData) {
    return (
      <SimpleMessageCard
        status={part.status}
        icon={Sparkles}
        title={displayName}
      />
    );
  }

  const content = (
    <div className="space-y-4">
      {!isEditing ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-foreground">
                {appNameData.displayName}
              </div>
              <div className="text-sm text-muted-foreground font-mono">
                {appNameData.slug}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Your app will be deployed at: https://{appNameData.slug}.fp.dev
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleEdit}
              className="ml-4"
            >
              <Edit3 className="h-4 w-4 mr-1" />
              Edit
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="displayName" className="text-sm font-medium">
              Display Name
            </Label>
            <Input
              id="displayName"
              value={editedDisplayName}
              onChange={(e) => setEditedDisplayName(e.target.value)}
              placeholder="My Awesome App"
              className="w-full"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="slug" className="text-sm font-medium">
              URL Slug
            </Label>
            <Input
              id="slug"
              value={editedSlug}
              onChange={(e) =>
                setEditedSlug(
                  e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                )
              }
              placeholder="my-awesome-app"
              className="w-full font-mono"
            />
            <div className="text-xs text-muted-foreground">
              Will be deployed at: https://{editedSlug || "your-app-name"}
              .fp.dev
            </div>
            {editedSlug && !validateAppSlug(editedSlug) && (
              <div className="text-xs text-destructive">
                {`Must be ${APP_NAME_SLUG_MIN_LENGTH}-${APP_NAME_SLUG_MAX_LENGTH_WITH_SUFFIX} characters, lowercase, alphanumeric with hyphens only`}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={
                isUpdating ||
                !editedDisplayName.trim() ||
                !validateAppSlug(editedSlug)
              }
            >
              <Check className="h-4 w-4 mr-1" />
              {isUpdating ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isUpdating}
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <ExpandableMessageCard
      status={part.status}
      result={part.result}
      icon={Sparkles}
      title={displayName}
      accordionValue="generate-app-name"
      expanded={true}
    >
      {content}
    </ExpandableMessageCard>
  );
}
